/**
 * Utilities for parsing and formatting server-side Zod validation errors
 * into human-readable field lists for the medication templates admin UI.
 *
 * Why:
 *   The server returns raw Zod error strings concatenated with semicolons.
 *   We parse the deterministic "field: error; field: error" format and convert
 *   dot-path field names to readable labels for display in a structured toast.
 *
 * How:
 *   Call `isZodValidationMessage(msg)` to test a raw error string, then pass
 *   it to `parseZodMessage(msg)` to get a `ZodFieldError[]` for display.
 */

export interface ZodFieldError {
  field: string;
  reason: string;
}

/**
 * Returns true if the message looks like a Zod validation string.
 * Requires both "expected" and "received" to avoid false-positives on
 * generic prose errors that might contain only one of those words.
 */
export const isZodValidationMessage = (msg: string): boolean =>
  msg.includes('expected ') && msg.includes('received ');

/**
 * Parses a server-side Zod validation message of the form
 *   "field1: error1; field2: error2; …"
 * into an array of ZodFieldError objects with human-readable labels.
 */
export const parseZodMessage = (raw: string): ZodFieldError[] =>
  raw
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((part) => {
      const colonIdx = part.indexOf(':');
      if (colonIdx === -1) return { field: part, reason: '' };
      return {
        field: formatFieldPath(part.slice(0, colonIdx).trim()),
        reason: classifyReason(part.slice(colonIdx + 1).trim())
      };
    });

/**
 * Converts a dot-path like "sigs[0].renewalFrequency.createAfterFirstDoseTaken"
 * into "Sigs 1 › Renewal Frequency › Create After First Dose Taken".
 */
const formatFieldPath = (path: string): string =>
  path
    .replace(/(\w+)\[(\d+)\]/g, (_, key, idx) => `${toLabel(key)} ${Number(idx) + 1}`)
    .split('.')
    .map(toLabel)
    .join(' › ');

const toLabel = (s: string): string =>
  s
    .replace(/([A-Z])/g, ' $1')
    .replace(/^\s/, '')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const classifyReason = (raw: string): string => {
  if (raw.includes('received null') || raw.includes('received undefined')) return 'required';
  if (raw.includes('expected number')) return 'must be a number';
  if (raw.includes('expected string')) return 'must be text';
  return 'invalid value';
};
